"use client"

import clsx from "clsx"
import { ReactNode, useRef } from "react"

// Accessibility:
// https://www.w3.org/WAI/ARIA/apg/patterns/accordion/

import { useId } from "react"
import { NpIcon } from "src/components/np-design/components/NpIcon"
import { AnimateHeight } from "src/components/unstyled/AnimateHeight"

export type NpAccordionItemData = {
  heading: string
  children: ReactNode
}

export type NpAccordionItemProps = NpAccordionItemData & {
  first: boolean
  last: boolean
  expanded: boolean
  onToggle: () => void
}

export const NpAccordionItem = ({ heading, children, expanded, first, last, onToggle }: NpAccordionItemProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const buttonId = useId()
  const panelId = useId()

  return (
    <li className="group border-np-green25 border-b last:border-0">
      <h2
        className={clsx(
          "text-default hover:bg-np-green10 m-0 w-full text-lg font-light transition-colors hover:transition-none",
          {
            "bg-np-green10": expanded,
            "rounded-t-lg": first,
            "rounded-b-lg": last,
          },
        )}
      >
        <button
          ref={buttonRef}
          type="button"
          id={buttonId}
          aria-controls={panelId}
          aria-expanded={expanded}
          className="flex min-h-15 w-full items-center gap-4 p-4 text-left text-lg lg:min-h-20 lg:text-2xl"
          onClick={onToggle}
        >
          <NpIcon
            name="add"
            className={clsx("size-8 transition-transform", {
              "rotate-135 transform": expanded,
            })}
          />

          <span>{heading}</span>
        </button>
      </h2>

      <div id={panelId} aria-labelledby={buttonId}>
        <AnimateHeight expanded={expanded}>
          <div className="px-6 py-4 lg:py-6 lg:pr-0 lg:pl-16">{children}</div>
        </AnimateHeight>
      </div>
    </li>
  )
}
